import { FilePlus2, Lasso, Loader2, Minimize2, Sparkles } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { keys } from "@/hooks/queries";
import { queryClient, type DrawingOut, type DrawingScene } from "@/lib/api";
import { useShell } from "@/stores/shell";

import type {
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/excalidraw/element/types";

import { PAGE_GAP, pagesFromAppState, type PageRect } from "./coords";
import {
  loadColorSlots,
  loadInk,
  resolveInk,
  saveColorSlots,
  saveInk,
  type InkSetting,
  type InkTool,
} from "./ink-settings";
import { buildPdfBackdrop } from "./pdf-pages";
import { captureRegion } from "./region-capture";
import { SaveController, sceneSignature } from "./save-controller";
import {
  fetchSceneFiles,
  putDrawingFile,
  toWireScene,
  uploadNewFiles,
} from "./scene-io";
import { WritingRail } from "./WritingRail";

/** The whole editor package rides one code-split chunk — loaded on first
 * surface mount, cached for the session. */
type ExcalidrawModule = typeof import("@excalidraw/excalidraw");
let modulePromise: Promise<ExcalidrawModule> | null = null;
export function loadExcalidraw(): Promise<ExcalidrawModule> {
  // The stylesheet rides the same lazy boundary as the code (0.18 requires an
  // explicit index.css import), so neither lands in the app shell.
  modulePromise ??= Promise.all([
    import("@excalidraw/excalidraw"),
    import("@excalidraw/excalidraw/index.css"),
  ]).then(([module]) => module);
  return modulePromise;
}

export interface DrawingSurfaceHandle {
  /** A PNG dataURL of the drawn content (content bbox), or null when empty —
   * feeds the canvas-note "Download as PDF" export. */
  exportPng: () => Promise<string | null>;
}

/** A "page" added to a canvas note is a native Excalidraw FRAME sized like a
 * PDF page (US-Letter at the scene scale), stacked below existing content. */
const PAGE_FRAME_W = 918;
const PAGE_FRAME_H = 1188;

const LASSO_STROKE_COLOR = "#7048e8";

interface ReadyState {
  module: ExcalidrawModule;
  initialElements: OrderedExcalidrawElement[];
  initialAppState: Record<string, unknown>;
  initialFiles: BinaryFiles;
  gmPages: PageRect[];
  /** Null when the backdrop was generated THIS open, so the first update()
   * saves it even before anything is drawn. */
  initialSignature: string | null;
  backdropCreated: boolean;
  /** The PAPER's lightness — what the sheet actually IS, not a display mode.
   * Drives adaptive ink, the writing rail's chrome, and Excalidraw's UI theme.
   *
   * Excalidraw's dark theme also paints the canvas through `invert(93%)
   * hue-rotate(180deg)` (it assumes scenes are authored light and flipped for
   * viewing). Our paper colour is literal, so that inversion would show a dark
   * note as light and its white ink as black — the inverse of what the
   * server-side flatten/export produces from the same scene. The surface's
   * `gm-true-colors` class neutralizes that filter (see styles.css), keeping
   * the dark chrome without the dark-mode colour flip. */
  paper: "light" | "dark";
}

/** The canvas color behind PDF pages — a neutral "desk" so the white sheets
 * read as paper. Without it the pages are seamless against a white canvas and
 * the surface looks like a solid color. Applied on every open BEFORE the
 * initial signature is computed, so it never dirties the scene by itself. */
const PDF_DESK_COLOR = "#e9ebef";

/** Perceived luminance of a hex color (0..1); malformed input reads as dark. */
function isLightColor(hex: string | undefined | null): boolean {
  if (typeof hex !== "string") return false;
  const v = hex.trim().replace("#", "");
  const full = v.length === 3 ? [...v].map((c) => c + c).join("") : v;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return false;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5;
}

function hasDrawnContent(scene: DrawingScene): boolean {
  // Locked elements are the surface's own furniture (PDF page backdrops, added
  // paper sheets) — only unlocked elements count as user content.
  return scene.elements.some((e) => !e.locked);
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("could not read the export"));
    reader.readAsDataURL(blob);
  });
}

/** The persisted appState subset out of a RESTORED (full) Excalidraw appState. */
function persistedFromRestored(appState: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (appState.viewBackgroundColor) out.viewBackgroundColor = appState.viewBackgroundColor;
  if (appState.gridSize !== undefined && appState.gridSize !== null) {
    out.gridSize = appState.gridSize;
  }
  if (typeof appState.gridModeEnabled === "boolean") {
    out.gridModeEnabled = appState.gridModeEnabled;
  }
  return out;
}

/** The Excalidraw annotate/write surface — the OPT-IN engine, not the default
 * one (the classic Fabric engine is; see stores/writing-engine.ts). All core
 * tools, the toolbar, undo/redo, and keyboard shortcuts are Excalidraw's own;
 * this component owns load/save (scene + externalized files), the PDF page
 * backdrop, and the layers the classic engine has that Excalidraw lacks: the
 * pen/highlighter rail (WritingRail), the "circle & ask AI" lasso, and page
 * frames for canvas notes. */
export const ExcalidrawSurface = forwardRef<
  DrawingSurfaceHandle,
  {
    docId: string;
    /** The loaded drawing (useDrawing). The surface mounts once it exists. */
    initial: DrawingOut;
    /** False = view-only (capability flag off): the scene renders, tools hide. */
    editable: boolean;
    /** Canvas notes: seeds viewBackgroundColor on a never-saved scene. */
    background?: string | null;
    /** PDFs: the file URL — first open renders each page as a locked image
     * backdrop at the canonical layout (coords.ts). */
    pdfUrl?: string;
    /** "Explain with AI": receives a PNG (of the circled region or the
     * selected elements) and the PDF page under it (1 for canvases). */
    onRegion?: (imageB64: string, page: number) => void;
    /** Fires when the scene gains/loses user-drawn content (drives the
     * "Annotated PDF" download gate). */
    onContentChange?: (hasDrawn: boolean) => void;
  }
>(function ExcalidrawSurface(
  { docId, initial, editable, background, pdfUrl, onRegion, onContentChange },
  ref,
) {
  const [ready, setReady] = useState<ReadyState | null>(null);
  const [failed, setFailed] = useState(false);
  const [api, setApi] = useState<ExcalidrawImperativeAPI | null>(null);
  // Fullscreen adds an exit control to our own island (see `controlIsland`).
  const fullscreen = useShell((s) => s.fullscreen);
  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const gmPagesRef = useRef<PageRect[]>([]);
  // fileIds known to exist server-side (uploads are skipped for these).
  const uploadedRef = useRef<Set<string>>(new Set());
  const saverRef = useRef<SaveController | null>(null);
  const troubleAt = useRef(0);
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  // The writing rail (OneNote-style pen/highlighter over Excalidraw's freedraw).
  const [ink, setInk] = useState<Record<InkTool, InkSetting>>(loadInk);
  const [colorSlots, setColorSlots] = useState<string[]>(loadColorSlots);
  const [railTool, setRailTool] = useState<InkTool>("pen");
  const [toolType, setToolType] = useState<string>("selection");
  // The armed "circle & ask AI" lasso session, if any.
  const lassoRef = useRef<{
    prevTool: string;
    prevInk: { color: string; width: number; opacity: number };
    before: Set<string>;
  } | null>(null);

  // Every callback handed to <Excalidraw> must be IDENTITY-STABLE: its layer
  // UI tunnels `renderTopRightUI` output through a component that setStates on
  // children changes, so a fresh element per render loops React to death
  // ("maximum update depth"). Parent-supplied callbacks and per-render state
  // are therefore read through refs from stable useCallbacks.
  const onRegionRef = useRef(onRegion);
  onRegionRef.current = onRegion;
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const inkRef = useRef(ink);
  inkRef.current = ink;
  const railToolRef = useRef(railTool);
  railToolRef.current = railTool;
  // The paper's lightness, read from a ref because the rail's handlers are
  // identity-stable but `ready` lands asynchronously.
  const paperRef = useRef<"light" | "dark">("light");
  paperRef.current = ready?.paper ?? "light";

  const wireFromEditor = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: Record<string, unknown>,
    ): DrawingScene => {
      const wire = toWireScene(elements, appState);
      if (gmPagesRef.current.length) wire.appState.gm_pages = gmPagesRef.current;
      return wire;
    },
    [],
  );

  // Load the editor chunk + the scene's files (+ first-open PDF backdrop), then
  // mount. `initial` is deliberately read once — after mount the editor is the
  // source of truth and a background refetch must not clobber it.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const module = await loadExcalidraw();
        const scene = initial.scene;
        const files = await fetchSceneFiles(docId, scene.file_ids);
        for (const id of Object.keys(files)) uploadedRef.current.add(id);

        let elements = scene.elements as unknown as OrderedExcalidrawElement[];
        let gmPages = pagesFromAppState(scene.appState);
        const appState: Record<string, unknown> = { ...scene.appState };
        let backdropCreated = false;

        if (pdfUrl && gmPages.length === 0) {
          // First annotate of this PDF: build the locked page backdrop.
          const backdrop = await buildPdfBackdrop(pdfUrl);
          if (cancelled) return;
          gmPages = backdrop.gmPages;
          backdropCreated = true;
          const backdropElements = module.convertToExcalidrawElements(
            backdrop.elementSkeletons as never,
          );
          // Upload the page PNGs now (idempotent by content hash) so the scene
          // is servable before the first stroke.
          await Promise.all(
            [...backdrop.files].map(async ([fileId, dataURL]) => {
              await putDrawingFile(docId, fileId, { mimeType: "image/png", dataURL });
              uploadedRef.current.add(fileId);
            }),
          );
          for (const [fileId, dataURL] of backdrop.files) {
            files[fileId as BinaryFileData["id"]] = {
              id: fileId as BinaryFileData["id"],
              mimeType: "image/png",
              dataURL: dataURL as BinaryFileData["dataURL"],
              created: Date.now(),
            };
          }
          // Backdrop sits underneath everything drawn later.
          elements = [...backdropElements, ...elements] as OrderedExcalidrawElement[];
        }

        if (!appState.viewBackgroundColor && background) {
          appState.viewBackgroundColor = background;
        }
        if (pdfUrl) {
          appState.viewBackgroundColor = PDF_DESK_COLOR;
        }

        // PDF backdrops are white pages regardless of view background, so a PDF
        // surface always reads as paper; canvases follow their own colour.
        const paper: "light" | "dark" =
          pdfUrl || isLightColor((appState.viewBackgroundColor as string) ?? "#ffffff")
            ? "light"
            : "dark";

        // Seed the rail's remembered ink through initialData, NOT through a
        // post-mount updateScene: Excalidraw applies initialData asynchronously
        // AFTER handing out its API, so a later write is clobbered by the scene
        // load resetting current-item state to Excalidraw's defaults — and the
        // change event that follows would then capture those defaults back over
        // the user's pen. Seeding here means the two agree from the first frame.
        const seed = inkRef.current[railToolRef.current];
        appState.currentItemStrokeColor = resolveInk(seed.color, paper);
        appState.currentItemStrokeWidth = seed.width;
        appState.currentItemOpacity = seed.opacity;

        const restored = module.restore(
          { elements, appState: appState as never },
          null,
          null,
        );
        const initialSignature = backdropCreated
          ? null
          : sceneSignature({
              schema_version: 1,
              elements: restored.elements.filter((e) => !e.isDeleted) as unknown as Record<
                string,
                unknown
              >[],
              appState: {
                ...persistedFromRestored(restored.appState as unknown as Record<string, unknown>),
                ...(gmPages.length ? { gm_pages: gmPages } : {}),
              },
              file_ids: [],
            });

        if (cancelled) return;
        gmPagesRef.current = gmPages;
        setReady({
          module,
          initialElements: restored.elements as unknown as OrderedExcalidrawElement[],
          initialAppState: restored.appState as unknown as Record<string, unknown>,
          initialFiles: files,
          gmPages,
          initialSignature,
          backdropCreated,
          paper,
        });
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
          toast.error("Couldn't open the drawing surface", { description: String(e) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, pdfUrl]);

  // The persistence controller lives exactly as long as the mounted scene.
  useEffect(() => {
    if (!ready) return;
    const saver = new SaveController({
      docId,
      initialSignature: ready.initialSignature,
      onTrouble: () => {
        const now = Date.now();
        if (now - troubleAt.current < 10_000) return;
        troubleAt.current = now;
        toast("Your drawing is saved here and will sync once you're back online.");
      },
      onReject: () => {
        toast.error("Couldn't save the drawing", {
          description: "The server rejected it. Please try that edit again.",
        });
      },
      onSaved: (scene) => {
        queryClient.setQueryData<DrawingOut>(keys.drawing(docId), {
          document_id: docId,
          scene,
          updated_at: new Date().toISOString(),
        });
      },
    });
    saverRef.current = saver;
    // A backdrop generated this open must persist even if nothing is drawn.
    if (ready.backdropCreated) {
      saver.update(wireFromEditor(ready.initialElements, ready.initialAppState));
    }
    return () => {
      saver.dispose();
      saverRef.current = null;
    };
  }, [ready, docId, wireFromEditor]);

  const pageForY = useCallback((cy: number): number => {
    for (const rect of gmPagesRef.current) {
      if (cy >= rect.y && cy <= rect.y + rect.h) return rect.page;
    }
    return 1;
  }, []);

  // The armed lasso resolves on pointer-up: the just-drawn freedraw stroke IS
  // the circled region — remove it, restore the pen, capture what it circled.
  useEffect(() => {
    if (!api || !ready) return;
    const off = api.onPointerUp(() => {
      const armed = lassoRef.current;
      if (!armed) return;
      // The element commits after the pointerup handlers run — defer a tick.
      setTimeout(() => {
        void (async () => {
          const current = apiRef.current;
          if (!current || lassoRef.current !== armed) return;
          const elements = current.getSceneElements();
          const stroke = elements.find(
            (e) => e.type === "freedraw" && !armed.before.has(e.id),
          );
          if (!stroke) return; // a stray tap — stay armed for the real circle
          lassoRef.current = null;
          current.updateScene({
            elements: elements.filter((e) => e.id !== stroke.id),
            appState: {
              currentItemStrokeColor: armed.prevInk.color,
              currentItemStrokeWidth: armed.prevInk.width,
              currentItemOpacity: armed.prevInk.opacity,
            } as never,
          });
          current.setActiveTool({
            type: (armed.prevTool === "freedraw" ? "freedraw" : "selection") as never,
          });
          const region = {
            x: stroke.x,
            y: stroke.y,
            w: Math.max(1, stroke.width),
            h: Math.max(1, stroke.height),
          };
          const image = await captureRegion(current, ready.module, region);
          if (!image) {
            toast("Circle a larger region and try again.");
            return;
          }
          onRegionRef.current?.(image.slice(image.indexOf(",") + 1), pageForY(region.y + region.h / 2));
        })();
      }, 30);
    });
    return off;
  }, [api, ready, pageForY]);

  const armLasso = useCallback(() => {
    const current = apiRef.current;
    if (!current || lassoRef.current) return;
    const appState = current.getAppState();
    lassoRef.current = {
      prevTool: appState.activeTool.type,
      prevInk: {
        color: appState.currentItemStrokeColor,
        width: appState.currentItemStrokeWidth,
        opacity: appState.currentItemOpacity,
      },
      before: new Set(current.getSceneElements().map((e) => e.id)),
    };
    current.updateScene({
      appState: {
        currentItemStrokeColor: LASSO_STROKE_COLOR,
        currentItemStrokeWidth: 1,
        currentItemOpacity: 70,
      } as never,
    });
    current.setActiveTool({ type: "freedraw" });
    toast("Circle the region you want explained — release to send it.");
  }, []);

  /** Push a remembered ink into Excalidraw and arm the draw tool. The adaptive
   * colour resolves HERE — against the paper — so what lands in the scene is a
   * real colour Excalidraw and the exporters both understand. */
  const applyInk = useCallback((setting: InkSetting) => {
    const current = apiRef.current;
    if (!current) return;
    current.updateScene({
      appState: {
        currentItemStrokeColor: resolveInk(setting.color, paperRef.current),
        currentItemStrokeWidth: setting.width,
        currentItemOpacity: setting.opacity,
      } as never,
    });
    // `locked` is what keeps the pen a pen. Excalidraw reverts to the SELECTION
    // tool once a drawn element is finalized unless the active tool is locked
    // (see its updateActiveTool / actionFinalize) — so writing several strokes
    // in quick succession would drop you into selection and the next stroke
    // would drag a selection box across the page instead of inking.
    current.setActiveTool({ type: "freedraw", locked: true });
  }, []);

  /** Merge a change into one tool's remembered ink, persist it, and (unless the
   * change CAME from Excalidraw's own panel) push it back to the editor. */
  const updateInk = useCallback(
    (tool: InkTool, patch: Partial<InkSetting>, opts: { apply: boolean }) => {
      setInk((prev) => {
        const next = { ...prev, [tool]: { ...prev[tool], ...patch } };
        inkRef.current = next;
        saveInk(next);
        if (opts.apply) applyInk(next[tool]);
        return next;
      });
    },
    [applyInk],
  );

  const pickTool = useCallback(
    (tool: InkTool) => {
      setRailTool(tool);
      railToolRef.current = tool;
      applyInk(inkRef.current[tool]);
    },
    [applyInk],
  );

  const pickWidth = useCallback(
    (width: number) => updateInk(railToolRef.current, { width }, { apply: true }),
    [updateInk],
  );

  const pickColor = useCallback(
    (color: string) => updateInk(railToolRef.current, { color }, { apply: true }),
    [updateInk],
  );

  /** Customizing a slot always targets the SELECTED slot (an unselected tap
   * selects instead of opening the editor), so the active tool takes the new
   * colour immediately — picking a colour means "write with this". */
  const customizeSlot = useCallback(
    (index: number, color: string) => {
      setColorSlots((prev) => {
        const next = prev.map((c, i) => (i === index ? color : c));
        saveColorSlots(next);
        return next;
      });
      updateInk(railToolRef.current, { color }, { apply: true });
    },
    [updateInk],
  );

  const addPageFrame = useCallback(() => {
    const current = apiRef.current;
    if (!current || !ready) return;
    const live = current.getSceneElements();

    if (gmPagesRef.current.length) {
      // PDF: an EXTRA sheet of paper below the last page — a locked white
      // rectangle at the next slot in the canonical page stack, recorded in
      // gm_pages so the annotated-PDF export appends it as a real page.
      const last = gmPagesRef.current[gmPagesRef.current.length - 1];
      if (!last) return;
      const rect = {
        page: last.page + 1,
        x: last.x,
        y: last.y + last.h + PAGE_GAP,
        w: last.w,
        h: last.h,
      };
      gmPagesRef.current = [...gmPagesRef.current, rect];
      const created = ready.module.convertToExcalidrawElements([
        {
          type: "rectangle",
          x: rect.x,
          y: rect.y,
          width: rect.w,
          height: rect.h,
          backgroundColor: "#ffffff",
          fillStyle: "solid",
          strokeColor: "transparent",
          roughness: 0,
          locked: true,
        } as never,
      ]);
      current.updateScene({ elements: [...live, ...created] });
      current.scrollToContent(created as never, { fitToViewport: true, animate: true } as never);
      return;
    }

    // Canvas note: a native Excalidraw FRAME — a named, letter-sized page
    // container stacked below existing content.
    const pageCount = live.filter((e) => e.type === "frame").length;
    let top = 0;
    if (live.length) {
      const [, , , maxY] = ready.module.getCommonBounds(live);
      top = maxY + PAGE_GAP;
    }
    const created = ready.module.convertToExcalidrawElements([
      {
        type: "frame",
        x: 0,
        y: top,
        width: PAGE_FRAME_W,
        height: PAGE_FRAME_H,
        name: `Page ${pageCount + 1}`,
        children: [],
      } as never,
    ]);
    current.updateScene({ elements: [...live, ...created] });
    current.scrollToContent(created as never, { fitToViewport: true, animate: true } as never);
  }, [ready]);

  useImperativeHandle(
    ref,
    () => ({
      exportPng: async () => {
        const current = apiRef.current;
        if (!ready || !current) return null;
        const elements = current.getSceneElements().filter((e) => !e.isDeleted);
        if (!elements.length) return null;
        const blob = await ready.module.exportToBlob({
          elements,
          files: current.getFiles(),
          mimeType: "image/png",
          appState: {
            exportBackground: true,
            viewBackgroundColor:
              (current.getAppState().viewBackgroundColor as string | undefined) ?? "#ffffff",
          },
          exportPadding: 24,
        });
        return blobToDataUrl(blob);
      },
    }),
    [ready],
  );

  const explainSelection = useCallback(async () => {
    const current = apiRef.current;
    if (!ready || !current) return;
    const appState = current.getAppState();
    const selectedIds = appState.selectedElementIds ?? {};
    const selected = current.getSceneElements().filter((e) => selectedIds[e.id]);
    if (!selected.length) {
      toast("Select something first — or use the lasso to circle a region.");
      return;
    }
    const [x1, y1, x2, y2] = ready.module.getCommonBounds(selected);
    const image = await captureRegion(current, ready.module, {
      x: x1 - 8,
      y: y1 - 8,
      w: x2 - x1 + 16,
      h: y2 - y1 + 16,
    });
    if (!image) {
      toast("Select something larger to explain.");
      return;
    }
    onRegionRef.current?.(image.slice(image.indexOf(",") + 1), pageForY((y1 + y2) / 2));
  }, [ready, pageForY]);

  const Excalidraw = ready?.module.Excalidraw;
  const initialData = useMemo(
    () =>
      ready
        ? {
            elements: ready.initialElements,
            appState: ready.initialAppState as never,
            files: ready.initialFiles,
            // PDFs get a page-1 fit below instead — centering ALL content at
            // 100% zoom lands the camera on the blank middle of sheet 1.
            scrollToContent: !pdfUrl,
          }
        : null,
    [ready, pdfUrl],
  );

  // Open a PDF like a reader: the whole first page in view. Excalidraw loads
  // initialData asynchronously after the API is handed out, so wait until the
  // page element is actually in the scene before fitting (fitting earlier gets
  // overwritten by the scene load resetting the camera).
  useEffect(() => {
    if (!api || !ready || !pdfUrl) return;
    const first = ready.gmPages[0];
    if (!first) return;
    let done = false;
    let raf = 0;
    const tick = () => {
      if (done) return;
      const page1 = api
        .getSceneElements()
        .find((e) => e.x === first.x && e.y === first.y && e.width === first.w);
      if (page1) {
        done = true;
        api.scrollToContent(page1 as never, { fitToContent: true, animate: false } as never);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    // A frame past element arrival, so the scene load's own camera reset has
    // already committed and can't overwrite the fit.
    raf = requestAnimationFrame(tick);
    return () => {
      done = true;
      cancelAnimationFrame(raf);
    };
  }, [api, ready, pdfUrl]);

  const setApiInstance = useCallback(
    (instance: ExcalidrawImperativeAPI) => {
      apiRef.current = instance;
      setApi(instance);
      if (import.meta.env.DEV && ready) {
        (window as unknown as { __gmDrawing?: unknown }).__gmDrawing = {
          api: instance,
          module: ready.module,
        };
      }
    },
    [ready],
  );

  const handleChange = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: Record<string, unknown> & {
        activeTool: { type: string };
        currentItemStrokeColor: string;
        currentItemStrokeWidth: number;
        currentItemOpacity: number;
      },
      files: BinaryFiles,
    ) => {
      setToolType(appState.activeTool.type);
      // Ink tuned in Excalidraw's OWN properties panel is captured back into the
      // rail's tool — "my pen" stays the pen you made it (OneNote behavior).
      // Never capture while the lasso is borrowing the freedraw tool with its
      // own ink, and never let a resolved adaptive colour overwrite the adaptive
      // SENTINEL: the pen is still "follows the paper", not literal black.
      if (appState.activeTool.type === "freedraw" && !lassoRef.current) {
        const tool = railToolRef.current;
        const remembered = inkRef.current[tool];
        const rememberedColor = resolveInk(remembered.color, paperRef.current);
        const patch: Partial<InkSetting> = {};
        if (appState.currentItemStrokeColor !== rememberedColor) {
          patch.color = appState.currentItemStrokeColor;
        }
        if (appState.currentItemStrokeWidth !== remembered.width) {
          patch.width = appState.currentItemStrokeWidth;
        }
        if (appState.currentItemOpacity !== remembered.opacity) {
          patch.opacity = appState.currentItemOpacity;
        }
        if (Object.keys(patch).length) updateInk(tool, patch, { apply: false });
      }
      const wire = wireFromEditor(elements, appState);
      onContentChangeRef.current?.(hasDrawnContent(wire));
      // Pasted/inserted images: push any new bytes before the scene that
      // references them lands (fire-and-forget; PUTs are idempotent).
      void uploadNewFiles(docId, wire, files, uploadedRef.current).catch(() => {});
      saverRef.current?.update(wire);
    },
    [docId, wireFromEditor, updateInk],
  );

  // Our controls float in their OWN island BELOW Excalidraw's top row rather
  // than being tunnelled into it.
  //
  // They used to ride `renderTopRightUI`, which put them inline with the Library
  // trigger — and at tablet widths that row overflowed the viewport: measured on
  // an 834px-wide tablet the exit-fullscreen button landed at x=856, i.e. off
  // the screen. Fullscreen then had no reachable exit at all, and a tablet has
  // no Esc key. Owning our own island bounds our width to our own controls and
  // leaves Excalidraw's row to Excalidraw.
  //
  // Exit-fullscreen is FIRST so the island's other buttons keep their position
  // relative to the right edge when it appears — the row grows leftward.
  const controlIsland = useMemo(
    () =>
      editable ? (
        <div className="pointer-events-none absolute right-2 top-[84px] z-10 flex justify-end">
          <div className="pointer-events-auto flex items-center gap-1">
          {fullscreen && (
            <Tip label="Exit fullscreen — Esc">
              <Button
                variant="outline"
                size="icon"
                onClick={() => useShell.getState().exitFullscreen()}
                aria-label="Exit fullscreen"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </Tip>
          )}
          {onRegion && (
            <Tip label="Circle & ask AI — lasso a region of the page to explain">
              <Button
                variant="outline"
                size="icon"
                onClick={armLasso}
                aria-label="Circle a region and ask AI"
              >
                <Lasso className="h-4 w-4 text-primary" />
              </Button>
            </Tip>
          )}
          {onRegion && (
            <Tip label="Explain the selected elements with AI — saved to Annotations">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void explainSelection()}
                aria-label="Explain selection with AI"
              >
                <Sparkles className="mr-1 h-3.5 w-3.5 text-primary" /> Explain
              </Button>
            </Tip>
          )}
          <Tip
            label={
              pdfUrl
                ? "Add a page — an extra sheet below the PDF (kept in the annotated export)"
                : "Add a page — a letter-sized frame below your content"
            }
          >
            <Button
              variant="outline"
              size="icon"
              onClick={addPageFrame}
              aria-label="Add a page"
            >
              <FilePlus2 className="h-4 w-4" />
            </Button>
          </Tip>
          </div>
        </div>
      ) : null,
    [editable, pdfUrl, !!onRegion, armLasso, explainSelection, addPageFrame, fullscreen], // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (failed) {
    return (
      <div className="grid h-full place-items-center p-6 text-center">
        <p className="text-xs text-muted-foreground">
          The drawing surface couldn’t be loaded. Reading still works — try again in a moment.
        </p>
      </div>
    );
  }
  if (!ready || !Excalidraw || !initialData) {
    return (
      <div className="grid h-full place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div
      ref={surfaceRef}
      className="gm-true-colors relative h-full min-h-0 w-full"
      data-testid="drawing-surface"
    >
      <Excalidraw
        excalidrawAPI={setApiInstance}
        initialData={initialData}
        theme={ready.paper}
        viewModeEnabled={!editable}
        UIOptions={UI_OPTIONS}
        onChange={handleChange as never}
      />
      {controlIsland}
      {editable && (
        <WritingRail
          tool={railTool}
          ink={ink}
          slots={colorSlots}
          paper={ready.paper}
          writing={toolType === "freedraw" && !lassoRef.current}
          onPickTool={pickTool}
          onPickWidth={pickWidth}
          onPickColor={pickColor}
          onCustomizeSlot={customizeSlot}
        />
      )}
    </div>
  );
});

// Module-level so its identity never changes across renders (Excalidraw
// re-renders on any prop identity change).
const UI_OPTIONS = {
  canvasActions: {
    loadScene: false,
    saveToActiveFile: false,
    export: false,
    saveAsImage: true,
    clearCanvas: true,
    changeViewBackgroundColor: true,
    toggleTheme: false,
  },
} as const;
